import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Button, Icon } from "@chakra-ui/react";
import { FaCrown } from "react-icons/fa";

/**
 * MembershipOfferBanner — the PRIMARY in-booking-flow membership offer, shown at
 * the price-reveal (Review) step to NON-members. It frames the plan as the
 * savings on THIS order (cheaper per-pound / included pounds), then routes to
 * the standalone Membership door to subscribe. Active members see nothing here
 * (they already get member pricing).
 *
 * Self-contained: fetches active plans + the customer's membership status by
 * (laundryId, customerId). Renders null unless there's a plan to pitch.
 */
const API = process.env.REACT_APP_AWS_API_URL;

export default function MembershipOfferBanner({ laundryId, customerId, subtotal = 0, estWeightLb = 0 }) {
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);
    const [hide, setHide] = useState(false);

    useEffect(() => {
        let alive = true;
        if (!laundryId) return;
        axios.get(`${API}/api/membership/plans`, { params: { laundryId, customerId } })
            .then((res) => {
                if (!alive) return;
                // Already a member → don't pitch.
                if (res.data?.membership) { setHide(true); return; }
                const plans = res.data?.plans || [];
                if (plans.length === 0) { setHide(true); return; }
                // Pitch the cheapest entry plan as the hook.
                const cheapest = [...plans].sort(
                    (a, b) => Number(a.monthlyPrice ?? a.monthly_price) - Number(b.monthlyPrice ?? b.monthly_price)
                )[0];
                setPlan(cheapest);
            })
            .catch(() => { if (alive) setHide(true); });
        return () => { alive = false; };
    }, [laundryId, customerId]);

    if (hide || !plan) return null;

    const price = Number(plan.monthlyPrice ?? plan.monthly_price);
    const allot = Number(plan.allotmentLb ?? plan.allotment_lb);
    const overage = Number(plan.overageRate ?? plan.overage_rate);
    const name = plan.name;

    // Estimated per-pound member cost vs. what this order costs now, when we can
    // estimate the order's weight. Kept intentionally simple + honest.
    let pitch = `Members get ${allot} lb/mo for $${price.toFixed(2)} (extra $${overage.toFixed(2)}/lb).`;
    if (estWeightLb > 0 && allot > 0) {
        const perLbNow = subtotal > 0 ? subtotal / estWeightLb : 0;
        if (perLbNow > 0) {
            const memberPerLb = price / allot;
            if (memberPerLb < perLbNow) {
                pitch = `This order is about $${perLbNow.toFixed(2)}/lb. ${name} members pay about ` +
                    `$${memberPerLb.toFixed(2)}/lb — subscribe and save on every load.`;
            }
        }
    }

    return (
        <Box mb={3} p={4} borderWidth="1px" borderRadius="lg" bg="purple.50" borderColor="purple.200">
            <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                <Flex align="center" gap={3}>
                    <Icon as={FaCrown} color="purple.500" boxSize={5} />
                    <Box>
                        <Text fontWeight="700" color="purple.800">Save with a {name} membership</Text>
                        <Text fontSize="sm" color="purple.700">{pitch}</Text>
                    </Box>
                </Flex>
                <Button size="sm" colorScheme="purple" onClick={() => navigate(`/${laundryId}/user/membership`)}>
                    See plans
                </Button>
            </Flex>
        </Box>
    );
}
